import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';

import { Box } from '@chakra-ui/react';
import {
  DISCIPLINARY_FILTER_HIDE_ON_PAGE_COLUMNS,
  DISCIPLINARY_FILTER_NON_REQUIRED_COLUMNS,
} from 'Deer/pages/Team/DirectReports/DisciplinaryActions/MyTeamsDisciplinaryActionsGrid';
import {
  DISCIPLINARY_ACTIONS_COLUMN_ORDER,
  DISCIPLINARY_ACTIONS_STATUS,
} from 'Deer/pages/Team/DirectReports/DisciplinaryActions/constants';
import { RowClickedEvent } from 'ag-grid-community';
import { useHistory } from 'react-router-dom';
import { useSelectedView } from 'reducers/viewsSlice';
import { CustomHeaderCell } from 'screens/Team/Rabbit/Goals/AgGridTreeView/components/CustomHeaderCell';
import { getColumnDefinitionByType } from 'screens/performanceReview/manageAndInsights/TalentPlanning/columnDefinitions';
import { AGGRID_COLUMN_TYPES } from 'screens/performanceReview/manageAndInsights/TalentPlanning/dataHooks';

import NoDisciplinaryActions from 'assets/assetsV1/illustrations/noDisciplinaryActions.svg';

import { isMobileView } from 'constants/isMobileView';
import { DISCIPLINARY_ACTIONS_INTENT_ID_MAP } from 'constants/moduleIntentTypes';

import { AgGridTable } from 'uiToolkitV1/AgGridWrapperV2';
import { AgGridKebabMenuOptions } from 'uiToolkitV1/AgGridWrapperV2/types';
import { EmptyScreen } from 'uiToolkitV1/EmptyScreen';

import { ReportedEmployee } from './components/ReportedEmployee';
import { useGetDisciplinaryActions } from './dataHooks';

// Proper TypeScript interfaces
interface MyDataDisciplinaryActionsGridProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  gridRef?: React.RefObject<any>;
  gridStatePersistenceKey: string;
  intentId?: string;
}

interface ColumnProps {
  columnId: string;
  displayName: string;
  description?: string;
  [key: string]: unknown;
}

interface ColumnDef {
  colId: string;
  field: string;
  id: string;
  displayName: string;
  description?: string;
  minWidth: number;
  [key: string]: unknown;
}

function MyDataDisciplinaryActionsGrid({
  gridRef,
  gridStatePersistenceKey,
  intentId,
}: MyDataDisciplinaryActionsGridProps) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const currentCompRef = useRef<any>();
  const agGridRef = gridRef ?? currentCompRef;
  const cohortDetails = useSelectedView();
  const history = useHistory();

  const [columnDefs, setColumnDefs] = useState<ColumnDef[]>([]);

  const {
    isFetching,
    data: disciplinaryActionsData,
    error,
  } = useGetDisciplinaryActions({
    cohortId: cohortDetails?.id,
    status: intentId === DISCIPLINARY_ACTIONS_INTENT_ID_MAP.ALL ? undefined : 'pending',
  });

  const { columns, rows = [] } = disciplinaryActionsData ?? {};

  // Memoize mobile view props to prevent unnecessary recalculations
  const mobileViewProps = useMemo(
    () =>
      isMobileView()
        ? {
            cellRenderer: ReportedEmployee,
            cellRendererParams: {
              screenType: 'EMPLOYEE',
            },
            autoHeight: true,
            cellStyle: { whiteSpace: 'normal' },
            headerComponent: CustomHeaderCell,
            headerComponentParams: {
              enableMenu: true,
              enableSorting: false,
            },
          }
        : {},
    []
  );

  // Define column-specific properties map
  const columnPropsMap = useMemo(
    () => ({
      RAISED_BY: {
        flex: 1,
        autoHeight: true,
        metadata: { isEmployeeSubtitle: true },
        ...mobileViewProps,
      },
      RAISED_FOR: {
        metadata: { isEmployeeSubtitle: true },
      },
      DESCRIPTION: {
        minWidth: 400,
      },
    }),
    [mobileViewProps]
  );

  useEffect(() => {
    if (!columns || columns.length === 0) return;

    const newColumnDefs = columns
      .map((col: ColumnProps) => ({
        colId: col.columnId,
        field: col.columnId,
        id: col.columnId,
        displayName: col.displayName,
        description: col.description,
        headerName: col.displayName,
        minWidth: 220,
        ...getColumnDefinitionByType(col),
        ...(columnPropsMap[col.columnId] || {}),

        // hiding on FE because email is visible in column cell, but keeping in column definition for csv download in separate column
        hide: DISCIPLINARY_FILTER_HIDE_ON_PAGE_COLUMNS.includes(col.columnId),

        suppressColumnsToolPanel: col.columnType === AGGRID_COLUMN_TYPES.EMAIL_INFO,
      }))
      .filter((col) => {
        if (isMobileView()) return col.field === 'RAISED_BY';
        if (col.field === 'NOTE_ID' || col.field === 'RAISED_FOR') return false;

        // ideally should not come in BE data, that's why hiding on FE for both table and report
        if (DISCIPLINARY_FILTER_NON_REQUIRED_COLUMNS.includes(col.colId)) return false;

        return true;
      })
      .sort(
        (a, b) =>
          DISCIPLINARY_ACTIONS_COLUMN_ORDER.findIndex((c) => c.colId === a.colId) -
          DISCIPLINARY_ACTIONS_COLUMN_ORDER.findIndex((c) => c.colId === b.colId)
      );

    setColumnDefs(newColumnDefs);
  }, [columns, mobileViewProps, columnPropsMap]);

  const openViolationDrawer = useCallback(
    (noteId: number) => {
      const queryParams = new URLSearchParams();
      queryParams.set('viewViolation', 'true');
      queryParams.set('violationNoteId', noteId.toString());
      history.push({ search: queryParams.toString() });
    },
    [history]
  );

  const onRowClicked = useCallback(
    (row: RowClickedEvent) => {
      const { data } = row;
      openViolationDrawer(data.NOTE_ID);
    },
    [openViolationDrawer]
  );

  // Handles acknowledging a disciplinary action
  const handleAcknowledge = useCallback(
    (noteId: number) => {
      openViolationDrawer(noteId);
    },
    [openViolationDrawer]
  );

  // Memoize kebab menu options generator to prevent unnecessary recreations
  const kebabMenuOptionsGenerator = useCallback(
    (data) => {
      const options: AgGridKebabMenuOptions = [];
      if (data?.STATUS === DISCIPLINARY_ACTIONS_STATUS.PUBLISHED_TO_EMPLOYEE) {
        const { NOTE_ID } = data;
        options.push({
          label: 'Acknowledge',
          onClick: () => handleAcknowledge(NOTE_ID),
        });
      }
      return options;
    },
    [handleAcknowledge]
  );

  // Memoize sidebar configuration for performance
  const sidebarConfig = useMemo(
    () =>
      isMobileView()
        ? false
        : {
            position: 'right' as const,
            toolPanels: ['columns'],
            defaultToolPanel: undefined,
          },
    []
  );

  // Memoize additionalMobileProps for performance
  const additionalMobileProps = useMemo(
    () => ({
      pinned: undefined,
      lockPosition: undefined,
    }),
    []
  );

  // Error state handling
  if (error) {
    return (
      <EmptyScreen
        heading="Actions not available!"
        description="There was an issue fetching the actions. Please try again later."
        illustrationSrc={NoDisciplinaryActions}
      />
    );
  }

  if (rows?.length === 0 && !isFetching) {
    return (
      <Box
        height="70vh"
        display="flex"
        justifyContent="center"
        alignItems="center"
        padding="16px 12px 8px 12px"
        flexDirection="column"
        gap="var(--size-300, 12px)"
        borderRadius="8px"
        background="var(--White, #FFF)"
        boxShadow="0px 0px 2px 0px rgba(0, 0, 0, 0.24)"
      >
        <EmptyScreen
          heading={
            intentId === DISCIPLINARY_ACTIONS_INTENT_ID_MAP.PENDING
              ? 'No pending disciplinary actions'
              : 'No disciplinary actions'
          }
          illustrationSrc={NoDisciplinaryActions}
        />
      </Box>
    );
  }

  return (
    <Box
      display="flex"
      padding="16px 12px 8px 12px"
      flexDirection="column"
      alignItems="flex-start"
      gap="var(--size-300, 12px)"
      borderRadius="8px"
      background="var(--White, #FFF)"
      boxShadow="0px 0px 2px 0px rgba(0, 0, 0, 0.24)"
    >
      <Box
        className="disciplinary-actions-grid all-disciplinary-actions-grid"
        width="100%"
        height="calc(100vh - 205px)"
      >
        <AgGridTable
          key={gridStatePersistenceKey}
          gridStatePersistenceKey={gridStatePersistenceKey}
          ref={agGridRef}
          rowData={rows}
          columnDefs={columnDefs}
          domLayout="normal"
          pagination
          paginationPageSize={10}
          paginationAutoPageSize={!!isMobileView()}
          headerHeight={isMobileView() ? 20 : 40}
          rowHeight={isMobileView() ? 186 : 48}
          rowStyle={{ cursor: 'default' }}
          kebabMenuOptionsGenerator={kebabMenuOptionsGenerator}
          kebabMenuIsRowSpecific={!isMobileView()}
          height="100%"
          sideBar={sidebarConfig}
          showSidebarToggle={false}
          /* In Mobile view, we have only one column, so pinning it left is creating empty space in center */
          additionalMobileProps={additionalMobileProps}
          loading={isFetching}
          onRowClicked={onRowClicked}
          defaultColDef={{
            resizable: false,
          }}
        />
      </Box>
    </Box>
  );
}

export { MyDataDisciplinaryActionsGrid };
